import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/payroll/salaries")({
  component: SalariesPage,
});

import { useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import PageHeader from "@/components/reusable-components/header/page-header";
import { SalaryTable } from "@/components/crud/table/SalaryTable";
import { SalaryFormSheet } from "@/components/crud/forms/SalaryFormSheet";

import useFetchSalaries from "@/hooks/api/salaries/useFetchSalaries";
import useCreateSalary from "@/hooks/api/salaries/useCreateSalary";
import useUpdateSalary from "@/hooks/api/salaries/useUpdateSalary";
import useDeleteSalary from "@/hooks/api/salaries/useDeleteSalary";
import useFetchSalaryHistory from "@/hooks/api/salaries/useFetchSalaryHistory";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";

import type { Salary, SalaryPayload } from "@/types/user/salary";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function SalariesPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "salary.salary.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const salariesQuery = useFetchSalaries({ page_size: pageSize, page });
  const salaries = salariesQuery.data?.results ?? [];
  const count = salariesQuery.data?.count ?? 0;

  const createSalary = useCreateSalary();
  const updateSalary = useUpdateSalary();
  const deleteSalary = useDeleteSalary();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingSalary, setEditingSalary] = useState<Salary | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [salaryToDelete, setSalaryToDelete] = useState<Salary | null>(null);

  const salaryHistoryQuery = useFetchSalaryHistory(editingSalary?.id, {
    enabled: isViewOnly && editingSalary != null,
  });
  const salaryHistories = useMemo(
    () => salaryHistoryQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [salaryHistoryQuery.data],
  );

  const [userSearchQuery, setUserSearchQuery] = useState("");
  const autoCompleteQuery = useAutoCompleteUser(
    { search: userSearchQuery || undefined, exclude_with_salary: true },
    { enabled: isSheetOpen }
  );
  const users = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data]
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  const isMutating =
    createSalary.isPending || updateSalary.isPending || deleteSalary.isPending;
  const isLoading = salariesQuery.isPending || isMutating;
  const isFetching = salariesQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleSearchUsers = useCallback((query: string) => {
    setUserSearchQuery(query);
  }, []);

  const handleCreate = () => {
    setEditingSalary(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (salary: Salary) => {
    setEditingSalary({ ...salary });
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (salary: Salary) => {
    setEditingSalary({ ...salary });
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const handleDelete = (salary: Salary) => {
    setSalaryToDelete(salary);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!salaryToDelete) return;
    try {
      await deleteSalary.mutateAsync({ id: salaryToDelete.id });
      toast.success("Salary deleted successfully");
      setIsDeleteDialogOpen(false);
      setSalaryToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: SalaryPayload) => {
    if (editingSalary) {
      await updateSalary.mutateAsync({ id: editingSalary.id, payload: data });
      toast.success("Salary updated successfully");
    } else {
      await createSalary.mutateAsync(data);
      toast.success("Salary created successfully");
    }
    setIsSheetOpen(false);
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Salaries"
          description="Manage employee salaries and view salary history."
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Add Salary"
          isLoading={isFetching}
        />

        <SalaryTable
          salaries={salaries}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
          canManage={canManage}
          onUserClick={(salary) =>
            navigate({
              to: "/admin/users/$id",
              params: { id: String(salary.user) },
              search: { tab: "salary" },
            })
          }
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <SalaryFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingSalary={editingSalary}
          salaryHistories={isViewOnly ? salaryHistories : []}
          hasMoreHistory={isViewOnly && salaryHistoryQuery.hasNextPage}
          isLoadingMoreHistory={salaryHistoryQuery.isFetchingNextPage}
          onLoadMoreHistory={() => salaryHistoryQuery.fetchNextPage()}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
          users={users}
          onSearchUsers={handleSearchUsers}
          autoCompleteLoading={autoCompleteLoading}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Salary"
          description={`Are you sure you want to delete the salary record for ${salaryToDelete?.user_full_name}? This action cannot be undone.`}
          itemName={salaryToDelete?.user_full_name}
          isLoading={deleteSalary.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
